import { Alert, Checkbox, Divider, FormControl, FormControlLabel, FormLabel, Typography } from '@mui/material';
import { NewsLetterCheckbox } from '../../NewsLetterCheckbox';
import { InterestsCheckbox } from '../../InterestsCheckbox';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import useI18nForm from '../hooks/useI18nForm';
import { useState } from 'react';
import LanguageRadioButtons from './LanguageRadioButton';
import i18n from 'i18n';
import RouterLink from 'common/hooks/RouterLink';
import FamiliplusPointBox from './FamiliplusPointBox';

export interface CommunicationPreferencesFormValues {
  newsletterConsent: boolean;
  newsletterTypes: string[];
  smsConsent: boolean;
  language: string;
  interestsChecked: string[];
  profilingConsent: boolean;
  hasMobilePhone: boolean;
}

interface CommunicationPreferencesFormProps {
  communicationPreferences: CommunicationPreferencesFormValues;
  saveHandler: (values: CommunicationPreferencesFormValues) => void;
  saveErrorMessage?: string | null;
  footer: JSX.Element;
  showNewsletterPtsBox: boolean;
  showInterestPtsBox: boolean;
  disableNewsletterContent: boolean | null;
  setNewsletterTypesSlug?: (value: string[] | ((prevVar: string[]) => string[])) => void;
  setInterestsSlug?: (value: string[] | ((prevVar: string[]) => string[])) => void;
  showSmsPtsBox: boolean;
}

const CommunicationPreferencesForm = ({
  disableNewsletterContent,
  communicationPreferences,
  saveHandler,
  saveErrorMessage,
  footer,
  showNewsletterPtsBox,
  showInterestPtsBox,
  setNewsletterTypesSlug,
  setInterestsSlug,
  showSmsPtsBox,
}: CommunicationPreferencesFormProps): JSX.Element => {
  const { t } = useTranslation('component');
  const [fetchErrorMsg, setFetchErrorMsg] = useState('');

  const formik = useFormik({
    initialValues: communicationPreferences,
    onSubmit: (values: CommunicationPreferencesFormValues) => {
      saveHandler(values);
    },
  });

  useI18nForm({ errors: formik.errors, touched: formik.touched, setFieldTouched: formik.setFieldTouched });

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      {(saveErrorMessage || fetchErrorMsg) && (
        <Alert data-cy="form-error" severity="error" sx={{ mb: '16px' }}>
          {t(saveErrorMessage ? saveErrorMessage : fetchErrorMsg)}
        </Alert>
      )}
      <LanguageRadioButtons formik={formik} showOptional={true} />
      <Divider />

      <NewsLetterCheckbox
        disableContentTypes={
          disableNewsletterContent == null
            ? !formik.values.newsletterConsent
            : formik.values.newsletterConsent == false
              ? disableNewsletterContent
              : !formik.values.newsletterConsent
        }
        formik={formik}
        setError={setFetchErrorMsg}
        showPtsBox={showNewsletterPtsBox}
        showOnlyNewsletterContent={false}
        setNewsletterTypesSlug={setNewsletterTypesSlug}
      />
      <Divider />

      {formik.values.hasMobilePhone && (
        <>
          <FormLabel id="SmsConsentLabel">
            SMS {t('Form.Optional')}
            {showSmsPtsBox && <FamiliplusPointBox points="50"></FamiliplusPointBox>}
          </FormLabel>
          <FormControlLabel
            sx={{ display: 'flex', alignItems: 'flex-start', mb: i18n.resolvedLanguage == 'fr' ? '5px' : '0' }}
            control={<Checkbox id="smsConsent" onChange={formik.handleChange} checked={formik.values.smsConsent} />}
            label={
              <Typography sx={{ position: 'relative', top: '9px' }} variant="body2">
                {t('CommunicationPreferences.SmsConsentLabelInfo')} {t('CommunicationPreferences.ClickToView')}{' '}
                <RouterLink
                  style={{ color: 'black' }}
                  target="_blank"
                  to={
                    i18n.resolvedLanguage == 'fr'
                      ? 'https://www.familiprix.com/fr/pages/politique-de-confidentialite'
                      : 'https://www.familiprix.com/en/pages/privacy-policy'
                  }>
                  {t('FooterLinkTitle.Privacy')}
                </RouterLink>{' '}
                {t('Prepositions.And')} {i18n.resolvedLanguage == 'fr' && t('Prepositions.Our')}{' '}
                <RouterLink
                  style={{ color: 'black' }}
                  target="_blank"
                  to={
                    i18n.resolvedLanguage == 'fr'
                      ? 'https://www.familiprix.com/fr/pages/termes-et-conditions'
                      : 'https://www.familiprix.com/en/pages/terms-and-conditions'
                  }>
                  {t('FooterLinkTitle.GeneralTermsService')}
                </RouterLink>
                .
              </Typography>
            }
          />

          <Divider />
        </>
      )}

      <FormLabel>
        {t('CommunicationPreferences.personalizationTitle')} {t('Form.Optional')}
      </FormLabel>
      <FormControl>
        <FormControlLabel
          sx={{ display: 'flex', alignItems: 'flex-start', mb: i18n.resolvedLanguage == 'fr' ? '5px' : '0' }}
          control={
            <Checkbox id="profilingConsent" onChange={formik.handleChange} checked={formik.values.profilingConsent} />
          }
          label={
            <Typography sx={{ position: 'relative', top: '9px' }} variant="body2">
              {t('CommunicationPreferences.PersonalizationLabelInfo')} {t('CommunicationPreferences.ClickToView')}{' '}
              <RouterLink
                style={{ color: 'black' }}
                to={
                  i18n.resolvedLanguage == 'fr'
                    ? 'https://www.familiprix.com/fr/pages/politique-de-confidentialite'
                    : 'https://www.familiprix.com/en/pages/privacy-policy'
                }>
                {t('FooterLinkTitle.Privacy')}
              </RouterLink>
              .
            </Typography>
          }
        />
      </FormControl>

      <Divider />

      <InterestsCheckbox
        formik={formik}
        setError={setFetchErrorMsg}
        showPtsBox={showInterestPtsBox}
        setInterestsSlug={setInterestsSlug}
      />

      <Divider />
      {footer}
    </form>
  );
};

export default CommunicationPreferencesForm;
